import { useState, type CSSProperties } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Pencil, Power } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Modal } from "@/components/ui/Modal.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { adminApi, type AdminCaptionStyle } from "@/api/admin.ts";
import { RENDERABLE_FONTS } from "@vms/tubeforge-shared";

// Caption vocabularies — mirror @vms/contracts CAPTION_* (kept local to avoid a
// contracts build dependency in the web app). The backend validates against the
// same sets; these drive the pickers and the authoring-time validity guard.
const ENTRY_OPTIONS = ["cut", "fade", "slide", "pop", "typewriter", "karaoke"] as const;
const EMPHASIS_OPTIONS = ["none", "colour", "scale", "underline", "box"] as const;
const PLACEMENT_OPTIONS = ["bottom", "middle", "top"] as const;
const EXIT_OPTIONS = ["cut", "fade", "slide", "hold"] as const;
const KIND_OPTIONS = ["word_sync", "paragraph_synced", "paragraph_read", "static_label"] as const;

// Which entry animations each kind may use (mirrors isCaptionEntryValidForKind).
// Per-word entries need a per-word timeline, so they are word_sync only;
// paragraph_synced may typewrite but not karaoke.
const KIND_ENTRY_VALIDITY: Record<string, readonly string[]> = {
  word_sync: ENTRY_OPTIONS,
  paragraph_synced: ["cut", "fade", "slide", "pop", "typewriter"],
  paragraph_read: ["cut", "fade", "slide", "pop"],
  static_label: ["cut", "fade", "slide", "pop"],
};
const isEntryValidForKind = (kind: string, entry: string) =>
  (KIND_ENTRY_VALIDITY[kind] ?? ENTRY_OPTIONS).includes(entry);

interface StyleForm {
  slug: string;
  name: string;
  description: string;
  font: string;
  color: string;
  strokeColor: string;
  fontSizeShorts: number;
  fontSizeLong: number;
  chunkSizeShorts: number;
  chunkSizeLong: number;
  animation: string;
  kind: string;
  emphasis: string;
  placement: string;
  exit: string;
  uppercase: boolean;
  sortOrder: number;
}

const emptyForm: StyleForm = {
  slug: "", name: "", description: "",
  font: "Montserrat", color: "#FFFFFF", strokeColor: "#000000",
  fontSizeShorts: 110, fontSizeLong: 72,
  chunkSizeShorts: 2, chunkSizeLong: 3,
  animation: "pop", kind: "word_sync", emphasis: "none",
  placement: "bottom", exit: "cut", uppercase: false, sortOrder: 0,
};

/** Live preview of what the caption will look like, reflecting emphasis and
 *  vertical placement as well as font/colour. */
function Swatch({ font, color, strokeColor, uppercase, emphasis, placement }: {
  font: string; color: string; strokeColor: string | null; uppercase: boolean;
  emphasis?: string; placement?: string;
}) {
  // The active-word treatment, mirroring the renderer's emphasisStyleFor.
  const emph: CSSProperties =
    emphasis === "colour" ? { color: "#FFD400" }
    : emphasis === "scale" ? { display: "inline-block", transform: "scale(1.12)" }
    : emphasis === "underline" ? { textDecoration: "underline", textUnderlineOffset: 2 }
    : emphasis === "box" ? { backgroundColor: "rgba(0,0,0,0.55)", padding: "0 0.15em", borderRadius: 3 }
    : {};
  const justify =
    placement === "top" ? "flex-start" : placement === "middle" ? "center" : "flex-end";
  return (
    <div
      className="flex h-12 w-24 shrink-0 justify-center rounded-md bg-black py-1"
      style={{ alignItems: justify }}
      title={`${emphasis ?? "none"} · ${placement ?? "bottom"}`}
    >
      <span
        style={{
          fontFamily: `"${font}", sans-serif`,
          fontWeight: 900,
          fontSize: 15,
          color,
          WebkitTextStroke: `1px ${strokeColor || "#000000"}`,
          textTransform: uppercase ? "uppercase" : "none",
          ...emph,
        }}
      >
        Aa
      </span>
    </div>
  );
}

export function AdminCaptionStylesPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState<"create" | "edit" | null>(null);
  const [form, setForm] = useState<StyleForm>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [showInactive, setShowInactive] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "caption-styles"],
    queryFn: () => adminApi.listCaptionStyles(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "caption-styles"] });

  const create = useMutation({
    mutationFn: () => adminApi.createCaptionStyle({ ...form, strokeColor: form.strokeColor || null }),
    onSuccess: () => { invalidate(); setModal(null); setForm(emptyForm); },
  });

  const update = useMutation({
    mutationFn: () => adminApi.updateCaptionStyle(editId!, { ...form, strokeColor: form.strokeColor || null }),
    onSuccess: () => { invalidate(); setModal(null); setEditId(null); setForm(emptyForm); },
  });

  const toggleActive = useMutation({
    mutationFn: (s: AdminCaptionStyle) => adminApi.updateCaptionStyle(s.id, { isActive: !s.isActive }),
    onSuccess: invalidate,
  });

  // Delete is refused server-side when a style is referenced by videos or series,
  // so surface that message rather than failing silently.
  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteCaptionStyle(id),
    onSuccess: () => { invalidate(); setDeleteId(null); setDeleteError(null); },
    onError: (err: unknown) => {
      const msg = (err as { message?: string })?.message || "Could not delete this style";
      setDeleteError(msg);
    },
  });

  const openEdit = (s: AdminCaptionStyle) => {
    setEditId(s.id);
    setForm({
      slug: s.slug, name: s.name, description: s.description ?? "",
      font: s.font, color: s.color, strokeColor: s.strokeColor ?? "",
      fontSizeShorts: s.fontSizeShorts, fontSizeLong: s.fontSizeLong,
      chunkSizeShorts: s.chunkSizeShorts, chunkSizeLong: s.chunkSizeLong,
      animation: s.animation, kind: s.kind, emphasis: s.emphasis,
      placement: s.placement, exit: s.exit,
      uppercase: s.uppercase, sortOrder: s.sortOrder,
    });
    setModal("edit");
  };

  const allStyles = data?.styles ?? [];
  const styles = allStyles.filter(
    (s) => (kindFilter === "all" || s.kind === kindFilter) && (showInactive || s.isActive),
  );
  const saving = create.isPending || update.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Caption Styles</h1>
          <p className="text-sm text-muted-foreground">
            Named looks users pick from when creating a video. Every app that renders through
            TubeForge reads this list.
          </p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setModal("create"); }}>
          <Plus className="mr-1.5 h-4 w-4" /> New style
        </Button>
      </div>

      {/* Browse filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
        >
          <option value="all">All kinds</option>
          {KIND_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <span className="text-xs text-muted-foreground">
          {styles.length} of {allStyles.length} shown
        </span>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : styles.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          No caption styles yet. Create one, or run the seed SQL for the six starters.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {styles.map((s) => (
            <Card key={s.id} className={s.isActive ? "" : "opacity-60"}>
              <CardContent className="flex items-center gap-4 py-3">
                <Swatch font={s.font} color={s.color} strokeColor={s.strokeColor}
                  uppercase={s.uppercase} emphasis={s.emphasis} placement={s.placement} />
                {/* The stored rendered thumbnail (Phase 4) — what actually ships
                    to downstream apps' pickers, beside the live CSS Swatch so an
                    admin can confirm the two agree. Absent until the preview job
                    has run for this style. */}
                {s.previewUrl && (
                  <img
                    src={s.previewUrl}
                    alt={`${s.name} rendered preview`}
                    className="h-12 w-7 shrink-0 rounded-md border border-border object-cover"
                    title="Rendered preview shipped to pickers"
                  />
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{s.name}</span>
                    <code className="text-xs text-muted-foreground">{s.slug}</code>
                    <Badge variant="muted">{s.kind}</Badge>
                    {s.isDefault && <Badge variant="muted">default</Badge>}
                    {!s.isActive && <Badge variant="muted">inactive</Badge>}
                  </div>
                  {s.description && (
                    <p className="mt-0.5 text-xs text-muted-foreground">{s.description}</p>
                  )}
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {s.font} · entry {s.animation} · emphasis {s.emphasis} ·{" "}
                    {s.placement} · exit {s.exit} · {s.chunkSizeShorts}/{s.chunkSizeLong} words ·{" "}
                    {s.fontSizeShorts}/{s.fontSizeLong}px
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  <Button
                    variant="ghost" size="sm"
                    title={s.isActive ? "Deactivate (hides it from pickers)" : "Activate"}
                    onClick={() => toggleActive.mutate(s)}
                  >
                    <Power className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" title="Edit" onClick={() => openEdit(s)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost" size="sm" title="Delete"
                    onClick={() => { setDeleteError(null); setDeleteId(s.id); }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / edit */}
      <Modal
        open={modal !== null}
        onClose={() => { setModal(null); setEditId(null); }}
        title={modal === "edit" ? "Edit caption style" : "New caption style"}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Slug</label>
              <Input
                value={form.slug}
                disabled={modal === "edit"}
                placeholder="bold_yellow"
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Name</label>
              <Input
                value={form.name}
                placeholder="Bold Yellow"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Description</label>
            <Input
              value={form.description}
              placeholder="High-contrast yellow, the short-form default look."
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Font</label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={form.font}
                onChange={(e) => setForm({ ...form, font: e.target.value })}
              >
                {/* Only fonts that actually render (RENDERABLE_FONTS from the
                    shared registry) — offering an unbundled font would render as
                    Impact fallback. If the saved value is a legacy/unbundled
                    font, keep it selectable so editing doesn't silently change it. */}
                {!RENDERABLE_FONTS.includes(form.font as never) && form.font && (
                  <option value={form.font}>{form.font} (not installed)</option>
                )}
                {RENDERABLE_FONTS.map((f) => (
                  <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Colour</label>
              <Input value={form.color} placeholder="#FFE600"
                onChange={(e) => setForm({ ...form, color: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Stroke</label>
              <Input value={form.strokeColor} placeholder="#000000 (blank = auto)"
                onChange={(e) => setForm({ ...form, strokeColor: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Size (shorts)</label>
              <Input type="number" value={form.fontSizeShorts}
                onChange={(e) => setForm({ ...form, fontSizeShorts: Number(e.target.value) })} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Size (long)</label>
              <Input type="number" value={form.fontSizeLong}
                onChange={(e) => setForm({ ...form, fontSizeLong: Number(e.target.value) })} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Words (shorts)</label>
              <Input type="number" value={form.chunkSizeShorts}
                onChange={(e) => setForm({ ...form, chunkSizeShorts: Number(e.target.value) })} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Words (long)</label>
              <Input type="number" value={form.chunkSizeLong}
                onChange={(e) => setForm({ ...form, chunkSizeLong: Number(e.target.value) })} />
            </div>
          </div>

          <div className="grid grid-cols-3 items-end gap-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Kind</label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={form.kind}
                onChange={(e) => {
                  const kind = e.target.value;
                  // Keep the combination valid: if the current entry animation is
                  // not allowed for the new kind, fall back to the first that is.
                  const animation = isEntryValidForKind(kind, form.animation)
                    ? form.animation
                    : (KIND_ENTRY_VALIDITY[kind] ?? ENTRY_OPTIONS)[0];
                  setForm({ ...form, kind, animation });
                }}
              >
                {KIND_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Animation (entry)</label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={form.animation}
                onChange={(e) => setForm({ ...form, animation: e.target.value })}
              >
                {ENTRY_OPTIONS.map((a) => (
                  <option key={a} value={a} disabled={!isEntryValidForKind(form.kind, a)}>
                    {a}{!isEntryValidForKind(form.kind, a) ? " (n/a for kind)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Emphasis</label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={form.emphasis}
                onChange={(e) => setForm({ ...form, emphasis: e.target.value })}
              >
                {EMPHASIS_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Placement</label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={form.placement}
                onChange={(e) => setForm({ ...form, placement: e.target.value })}
              >
                {PLACEMENT_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Exit</label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={form.exit}
                onChange={(e) => setForm({ ...form, exit: e.target.value })}
              >
                {EXIT_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Sort order</label>
              <Input type="number" value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} />
            </div>
            <label className="flex h-9 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.uppercase}
                onChange={(e) => setForm({ ...form, uppercase: e.target.checked })}
              />
              Uppercase
            </label>
          </div>

          {/* Preview reflects the form as you type */}
          <div className="flex items-center gap-3 rounded-md border p-3">
            <Swatch
              font={form.font}
              color={form.color}
              strokeColor={form.strokeColor || null}
              uppercase={form.uppercase}
            />
            <span className="text-xs text-muted-foreground">Live preview</span>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => { setModal(null); setEditId(null); }}>Cancel</Button>
            <Button
              disabled={saving || !form.slug || !form.name}
              onClick={() => (modal === "edit" ? update.mutate() : create.mutate())}
            >
              {saving ? "Saving…" : modal === "edit" ? "Save changes" : "Create style"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal open={deleteId !== null} onClose={() => { setDeleteId(null); setDeleteError(null); }} title="Delete caption style">
        <div className="space-y-3">
          <p className="text-sm">
            Delete this style? Videos already using it would fall back to preset defaults.
          </p>
          <p className="text-sm text-muted-foreground">
            The in-use check only covers TubeForge's own videos and series — downstream apps
            (Cliptura, Sotreni, SellerZoom) keep their own copies and aren't visible here.
            <strong className="text-foreground"> Deactivating is safer:</strong> it removes the
            style from every app on their next sync, while a delete leaves stale references
            behind (they fall back to defaults, but silently).
          </p>
          {deleteError && <p className="text-sm text-destructive">{deleteError}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setDeleteId(null); setDeleteError(null); }}>Cancel</Button>
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => remove.mutate(deleteId!)}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
